// The line under an assistant's name in the store menu and the Overview: its site, said once when
// the assistant is still named by its domain, then how far it is (published, ready, reading,
// stopped) and anything the place adds, such as the published version.
import { formatCopy } from '@/i18n/ns';
import type { StorefrontCopyKey } from '@/i18n/ns/storefront';
import type { DiscoveryRun, StoreMarket, StoreSummary } from './api';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;

export const storeState = (store: Pick<StoreSummary, 'status'>, copy: Copy) =>
  store.status === 'live'
    ? copy.storeLive
    : store.status === 'ready'
      ? copy.storeReady
      : store.status === 'failed'
        ? copy.storeStopped
        : copy.storeReading;

export const storeLine = (
  store: Pick<StoreSummary, 'name' | 'domain' | 'status'>,
  copy: Copy,
  extra: (string | null)[] = [],
) =>
  [store.name === store.domain ? null : store.domain, storeState(store, copy), ...extra]
    .filter(Boolean)
    .join(' · ');

const SCOPED: Record<string, StorefrontCopyKey> = {
  product_cards: 'readingProducts',
  cart_functions: 'readingCart',
  support_widget: 'readingChat',
  market: 'readingMarket',
};

/** What a read that covers part of the store is doing ("Reading the products again"); null for a whole read. */
export function runScopeText(
  run: Pick<DiscoveryRun, 'scope' | 'market' | 'checkOnly'> | null | undefined,
  markets: StoreMarket[] | undefined,
  copy: Copy,
) {
  // A check of saved work again reads nothing new, whatever scope the read it checks had.
  if (run?.checkOnly === true) return copy.checkingSavedWork;
  const key = run?.scope ? SCOPED[run.scope] : undefined;
  if (!key) return null;
  if (run?.scope === 'market' && !run.market) return copy.readingMarketAny;
  const market = markets?.find((entry) => entry.code === run?.market);
  return formatCopy(copy[key], { market: market?.name ?? (run?.market ?? '').toUpperCase() });
}
